import React from 'react';
import { Box, Text, Heading, SimpleGrid, VStack } from '@chakra-ui/react';
import { BinderSectionData } from '../hooks/useBinderSectionData';
import { useBinderCardColors } from '../hooks/useBinderCardColors';

interface ListedDriversCardProps {
  sectionData: BinderSectionData;
}

const ListedDriversCard: React.FC<ListedDriversCardProps> = ({
  sectionData,
}) => {
  const { data } = sectionData;
  const { drivers = [], insuranceSource } = data || {};
  const colors = useBinderCardColors();

  // Handle case when no drivers
  if (drivers.length === 0) {
    return (
      <Box p={4} borderRadius="md" bg={colors.emptyFieldBgColor}>
        <Text color="gray.500" fontStyle="italic" textAlign="center">
          No driver information available
        </Text>
      </Box>
    );
  }

  const renderField = (
    label: string,
    value: string,
    isEmpty: boolean = false,
  ) => (
    <VStack align="start" spacing={1}>
      <Text
        fontSize="xs"
        fontWeight="semibold"
        color={colors.labelColor}
        textTransform="uppercase"
        letterSpacing="wide"
      >
        {label}
      </Text>
      <Box>
        {isEmpty || value === 'N/A' ? (
          <Text color="gray.400" fontStyle="italic" fontSize="sm">
            -
          </Text>
        ) : (
          <Text
            fontSize="sm"
            fontWeight="medium"
            color={colors.textColor}
            wordBreak="break-word"
          >
            {value}
          </Text>
        )}
      </Box>
    </VStack>
  );

  return (
    <Box>
      <Box mb={4}>
        <Heading size="sm" color={colors.textColor} mb={2}>
          Listed Drivers
        </Heading>
        <Text fontSize="sm" color={colors.descriptionColor}>
          {drivers.length} driver(s) listed
        </Text>
      </Box>

      <VStack spacing={6} align="stretch">
        {drivers.map((driver: any, driverIndex: number) => (
          <Box
            key={driverIndex}
            borderRadius="md"
            borderWidth="1px"
            borderColor={colors.borderColor}
            overflow="hidden"
          >
            {/* Row 1: Name (1 column) */}
            <Box
              p={4}
              bg="transparent"
              borderBottomWidth="1px"
              borderColor={colors.borderColor}
            >
              {renderField('Driver Name', driver.name)}
            </Box>

            {/* Row 2: License Info (4 columns) */}
            <Box bg={colors.emptyFieldBgColor}>
              <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={0}>
                <Box
                  p={4}
                  borderRightWidth={{ base: 0, lg: '1px' }}
                  borderBottomWidth="1px"
                  borderColor={colors.borderColor}
                >
                  {renderField(
                    'Drivers License Number',
                    driver.driversLicenseNumber,
                  )}
                </Box>
                <Box
                  p={4}
                  borderRightWidth={{ base: 0, lg: '1px' }}
                  borderBottomWidth="1px"
                  borderColor={colors.borderColor}
                >
                  {renderField('License Class', driver.licenseClass)}
                </Box>
                <Box
                  p={4}
                  borderRightWidth={{ base: 0, lg: '1px' }}
                  borderBottomWidth="1px"
                  borderColor={colors.borderColor}
                >
                  {renderField('Principal', driver.principal)}
                </Box>
                <Box
                  p={4}
                  borderBottomWidth="1px"
                  borderColor={colors.borderColor}
                >
                  {renderField('Excluded', driver.excluded)}
                </Box>
              </SimpleGrid>
            </Box>

            {/* Row 3: Dates and Status (3 columns) */}
            <Box bg="transparent">
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={0}>
                <Box
                  p={4}
                  borderRightWidth={{ base: 0, md: '1px' }}
                  borderBottomWidth="1px"
                  borderColor={colors.borderColor}
                >
                  {renderField('MVR Date', driver.mvrDate)}
                </Box>
                <Box
                  p={4}
                  borderRightWidth={{ base: 0, md: '1px' }}
                  borderBottomWidth="1px"
                  borderColor={colors.borderColor}
                >
                  {renderField('Status', driver.status)}
                </Box>
                <Box
                  p={4}
                  borderBottomWidth="1px"
                  borderColor={colors.borderColor}
                >
                  {renderField(`${insuranceSource} Date`, driver.autoPlusDate)}
                </Box>
              </SimpleGrid>
            </Box>

            {/* Row 4: Experience (3 columns) */}
            <Box bg={colors.emptyFieldBgColor}>
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={0}>
                <Box
                  p={4}
                  borderRightWidth={{ base: 0, md: '1px' }}
                  borderBottomWidth="1px"
                  borderColor={colors.borderColor}
                >
                  {renderField('Years', driver.years)}
                </Box>
                <Box
                  p={4}
                  borderRightWidth={{ base: 0, md: '1px' }}
                  borderBottomWidth="1px"
                  borderColor={colors.borderColor}
                >
                  {renderField('Months', driver.months)}
                </Box>
                <Box
                  p={4}
                  borderBottomWidth="1px"
                  borderColor={colors.borderColor}
                >
                  {renderField('Gaps', driver.gaps)}
                </Box>
              </SimpleGrid>
            </Box>

            {/* Row 5: Previous Insurance (4 columns) */}
            <Box bg="transparent">
              <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={0}>
                <Box
                  p={4}
                  borderRightWidth={{ base: 0, lg: '1px' }}
                  borderBottomWidth="1px"
                  borderColor={colors.borderColor}
                >
                  {renderField('Previous Company', driver.previousCompany)}
                </Box>
                <Box
                  p={4}
                  borderRightWidth={{ base: 0, lg: '1px' }}
                  borderBottomWidth="1px"
                  borderColor={colors.borderColor}
                >
                  {renderField(
                    'Previous Policy Number',
                    driver.previousPolicyNumber,
                  )}
                </Box>
                <Box
                  p={4}
                  borderRightWidth={{ base: 0, lg: '1px' }}
                  borderBottomWidth="1px"
                  borderColor={colors.borderColor}
                >
                  {renderField('Effective Date/Time', driver.effectiveDateTime)}
                </Box>
                <Box
                  p={4}
                  borderBottomWidth="1px"
                  borderColor={colors.borderColor}
                >
                  {renderField('Expiry Date/Time', driver.expiryDateTime)}
                </Box>
              </SimpleGrid>
            </Box>

            {/* Row 6: Declarations (2 columns) */}
            <Box bg={colors.emptyFieldBgColor}>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={0}>
                <Box
                  p={4}
                  borderRightWidth={{ base: 0, md: '1px' }}
                  borderColor={colors.borderColor}
                >
                  {renderField('OPCF 28A', driver.opcf28a)}
                </Box>
                <Box p={4}>
                  {renderField(
                    'Retiree Declaration',
                    driver.retireeDeclaration,
                  )}
                </Box>
              </SimpleGrid>
            </Box>
          </Box>
        ))}
      </VStack>
    </Box>
  );
};

export default ListedDriversCard;
